/**
 * @fileoverview Profile-aware field and requirement helpers.
 * @module utils/profileFieldUtils
 */

/** Prefix for business-event requirement keys that map to one certification checkbox index. */
export const CERTIFICATION_OPTION_REQUIREMENT_PREFIX =
  "certificationOptionRequirement_";

export const getProfileByNumber = (company, profileNumber) => {
  if (!company?.perfiles || !Array.isArray(company.perfiles)) {
    return null;
  }
  return (
    company.perfiles.find(
      (profile) => profile?.numeroDePerfil === profileNumber,
    ) || null
  );
};

export const getProfileFieldValue = (
  company,
  fieldName,
  selectedProfile = 0,
  defaultValue = "",
) => {
  if (!company) {
    return defaultValue;
  }
  if (selectedProfile === 0) {
    return company[fieldName] ?? defaultValue;
  }

  const profile = getProfileByNumber(company, selectedProfile);
  if (!profile) {
    return defaultValue;
  }
  if (Object.prototype.hasOwnProperty.call(profile, fieldName)) {
    return profile[fieldName];
  }
  return defaultValue;
};

export const PROFILE_EDITABLE_FIELDS = [
  "ambitoOperaciones",
  "actividadGeneralDeLaEmpresa",
  "palabrasClave",
  "descripcionDeLaEmpresa",
  "telefonoDeOficina",
  "telefonoDeOficinaCodigoPais",
  "mostrarTelefonoDeOficina",
  "extensionTelefono",
  "mostrarExtensionTelefono",
  "telefonoSecundario",
  "telefonoSecundarioCodigoPais",
  "mostrarTelefonoSecundario",
  "correoElectronicoPrincipal",
  "mostrarCorreoElectronicoPrincipal",
  "paginaInternet",
  "mostrarPaginaInternet",
  "contactos",
  "sucursales",
  "productoPrincipal",
  "materiaPrima",
  "exporta",
  "paisExportacion",
  "importa",
  "paisImportacion",
  "vendeAGobierno",
  "certificacionesDeCalidad",
  "maquinariaYEquipo",
  "procesosYEspecialidades",
  "clientes",
  "linkedIn",
  "mostrarLinkedIn",
  "facebook",
  "mostrarFacebook",
  "twitter",
  "mostrarTwitter",
  "youtube",
  "mostrarYoutube",
  "instagram",
  "mostrarInstagram",
  "documentos",
  "brochure",
  "polizaDeResponsabilidadCivil",
  "comprobanteDeCertificacion",
  "certificaciones",
  "capacidadInstalada",
  "esProveedorREPSE",
  "tiemposPromedioDeEntrega",
  "volumenDeVentasAnuales",
  "metodosDePagoAceptados",
  "seguroDeResponsabilidadCivil",
  "permisoIMMEX",
  "practicasImplementadas",
  "sectores",
  "clientesPrincipalesYReferenciasComerciales",
  "tieneSistemaDocumentado",
];

export const isProfileEditableField = (fieldName) =>
  PROFILE_EDITABLE_FIELDS.includes(fieldName);

export const getCompanyFieldValue = (
  company,
  fieldName,
  selectedProfile = 0,
  defaultValue = "",
) => {
  if (!company) {
    return defaultValue;
  }
  if (!isProfileEditableField(fieldName)) {
    return company[fieldName] ?? defaultValue;
  }
  return getProfileFieldValue(
    company,
    fieldName,
    selectedProfile,
    defaultValue,
  );
};

export const parseCertificationOptionRequirementKey = (fieldKey) => {
  if (
    typeof fieldKey !== "string" ||
    !fieldKey.startsWith(CERTIFICATION_OPTION_REQUIREMENT_PREFIX)
  ) {
    return null;
  }
  const optionIndex = Number(
    fieldKey.slice(CERTIFICATION_OPTION_REQUIREMENT_PREFIX.length),
  );
  return Number.isInteger(optionIndex) && optionIndex >= 0 ? optionIndex : null;
};

/**
 * Reads a requirement field, including certification-option keys.
 * `defaultValue` comes before `selectedProfile` so the defaulted argument is last.
 * @param {object|null|undefined} entity
 * @param {string} fieldKey
 * @param {*} defaultValue
 * @param {number} [selectedProfile=0]
 * @returns {*}
 */
export const getRequirementFieldValue = (
  entity,
  fieldKey,
  defaultValue,
  selectedProfile = 0,
) => {
  if (!entity) {
    return defaultValue;
  }
  const certificationIndex =
    parseCertificationOptionRequirementKey(fieldKey);
  if (certificationIndex !== null) {
    const certifications = getCompanyFieldValue(
      entity,
      "certificaciones",
      selectedProfile,
      [],
    );
    return Array.isArray(certifications)
      ? certifications.includes(certificationIndex)
      : defaultValue;
  }
  return getCompanyFieldValue(entity, fieldKey, selectedProfile, defaultValue);
};

export const PROFILE_HIDDEN_FIELDS = [
  "numeroSocio",
  "grupoEmpresarial",
  "fechaInicioOperaciones",
  "fechaAltaSac",
  "cedulaFiscal",
  "opinionDeCumplimientoPositiva",
  "altaIMSS",
  "esProveedorREPSE",
  "sectorEconomico",
  "ramaSCIAN",
  "subsector",
  "areasDeInteres",
  "ejecutivoDeAtencion",
  "fechaUltimoPago",
  "perteneceAUnaAsociacionEmpresarial",
  "asociacionEmpresarial",
  "razonSocial",
];

export const shouldHideFieldForProfile = (fieldName, selectedProfile) =>
  selectedProfile !== 0 && PROFILE_HIDDEN_FIELDS.includes(fieldName);

export const PROFILE_READONLY_FIELDS = [
  "nombreComercial",
  "tamanoDeLaEmpresa",
  "logo",
  "direccion",
];

export const isFieldReadOnlyForProfile = (fieldName, selectedProfile) =>
  selectedProfile !== 0 && PROFILE_READONLY_FIELDS.includes(fieldName);
